import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Oct 2026 — phone-only "swipe for more" hint for horizontal chip rows.
// Desktop shows a scrollbar; on a phone only one card fits, so nothing
// signals that more cards sit off-screen. Hint text tracks scroll position:
//   start  → "‹ Swipe Left for More"
//   middle → "‹ Swipe Left or Right for More ›"
//   end    → "Swipe Right for More ›"
// Returns null (no hint) when the row doesn't overflow.
export function useSwipeHint(ref, deps = []) {
  const [hint, setHint] = useState(null);
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 4) { setHint(null); return; }
    const x = el.scrollLeft;
    setHint(x <= 24 ? 'start' : x >= max - 24 ? 'end' : 'middle');
  }, [ref]);
  useEffect(() => {
    update();
    const id = setTimeout(update, 300);
    window.addEventListener('resize', update);
    return () => { clearTimeout(id); window.removeEventListener('resize', update); };
  }, [update, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return [hint, update];
}

export default function SwipeHint({ hint, testId, className = '' }) {
  if (!hint) return null;
  return (
    <div
      className={`sm:hidden flex items-center justify-center gap-1 mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ${className}`}
      data-testid={testId}
      data-hint={hint}
      aria-live="polite"
    >
      {hint !== 'end' && <ChevronLeft className="w-3 h-3" />}
      <span>{hint === 'start' ? 'Swipe Left for More' : hint === 'middle' ? 'Swipe Left or Right for More' : 'Swipe Right for More'}</span>
      {hint !== 'start' && <ChevronRight className="w-3 h-3" />}
    </div>
  );
}
